import { useState, useMemo, useEffect, useContext } from "react";
import {
    Box, Typography, Grid, MenuItem, Select,
    FormControl, InputLabel, CircularProgress, Alert, Paper, Divider
} from "@mui/material";
import { DateRangePicker, TagPicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import dayjs from "dayjs";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import { EntityContext } from "../../../../contexts/EntityContext";
import reportsApi from "../../../../shared/utils/reportsApi";
import "./styles.css";

const PROPERTIES = [
    { id: "11", title: "Balboa Bay Resort" },
    { id: "2", title: "Anaheim Portofino Inn & Suites" },
    { id: "181", title: "Dollywood HeartSong Lodge and Resort" },
    { id: "14", title: "Boston Harbor Hotel" },
    { id: "7", title: "Autocamp Catskills" },
    { id: "103", title: "The Hermitage Hotel" },
    { id: "9", title: "Autocamp Russian River" },
    { id: "23", title: "Eau Palm Beach Resort & Spa" },
    { id: "26", title: "Enchantment Resort" },
    { id: "60", title: "Mii amo" },
    { id: "21", title: "Dollywood DreamMore Resort" },
    { id: "8", title: "Autocamp Joshua Tree" },
    { id: "27", title: "Evo Hotel Tahoe City" },
    { id: "3", title: "Arnold Palmer's Bay Hill Club & Lodge" },
    { id: "73", title: "Parmer's Resort" }
];

const CHANNEL_OPTIONS = ["SA360", "FacebookAds", "GoogleAds", "DV360", "CM360", "Derbysoft"].map(v => ({ label: v, value: v }));
const COLORS = ["#1976d2", "#2e7d32", "#ed6c02", "#9c27b0", "#d32f2f", "#00838f", "#795548"];

const ChannelRollup = () => {
    const { setEntityName } = useContext(EntityContext);
    const [propertyId, setPropertyId] = useState<string>("2");
    const [channels, setChannels] = useState<string[]>([]);
    const [dateRange, setDateRange] = useState<[Date, Date] | null>([
        dayjs().subtract(30, 'day').toDate(),
        dayjs().toDate()
    ]);

    const [response, setResponse] = useState<{ loading: boolean, data: any, error: any }>({
        loading: false,
        data: null,
        error: null
    });

    useEffect(() => {
        setEntityName({ entityName: "ChannelRollup", entityTitle: "Channel Rollup" });
    }, [setEntityName]);

    const fetchChannelData = async () => {
        if (!propertyId || !dateRange) return;
        setResponse(prev => ({ ...prev, loading: true, error: null }));
        
        const startDate = dayjs(dateRange[0]).format("YYYY-MM-DD");
        const endDate = dayjs(dateRange[1]).format("YYYY-MM-DD");

        try {
            const res = await reportsApi.get(`/reports/channel-rollups`, {
                params: {
                    property_id: propertyId,
                    startDate,
                    endDate,
                    channel_filters: channels.length > 0 ? channels : undefined,
                    offset: 0,
                    limit: 100
                },
                headers: { "Cache-Control": "no-cache" }
            });

            if (!res.data || (Object.keys(res.data).length === 0 && localStorage.getItem("gcomToken"))) {
                 fetchChannelData();
                 return;
            }

            setResponse({ loading: false, data: res.data, error: null });
        } catch (err: any) {
            setResponse({ loading: false, data: null, error: err.message });
        }
    };

    useEffect(() => {
        fetchChannelData();
    }, [propertyId, channels, dateRange]);

    const chartData = useMemo(() => {
        if (!response.data?.items?.[0]?.channels) return [];
        return response.data.items[0].channels.map((c: any) => ({
            name: c.data_source,
            clicks: parseInt(c.clicks || 0),
            impressions: parseInt(c.impressions || 0),
            cost: parseFloat(c.cost || 0),
            revenue: parseFloat(c.revenue || 0),
            bookings: parseInt(c.bookings || 0),
            roas: parseFloat(c.roas || 0)
        }));
    }, [response.data]);

    const renderBarChart = (title: string, dataKey: string, isCurrency = false) => (
        <Grid item xs={12} md={6} lg={4}>
            <Paper elevation={1} sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: '12px', bgcolor: '#fff' }}>
                <Box className="graphHeader" sx={{ mb: 2, mx: -2, mt: -2, borderTopLeftRadius: '12px', borderTopRightRadius: '12px', bgcolor: '#f7f7f7' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333' }}>{title}</Typography>
                </Box>
                <Box sx={{ height: 280, width: "100%", mt: 1 }}>
                    <ResponsiveContainer>
                        <BarChart data={chartData} margin={{ top: 20, right: 10, left: -10, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                            <XAxis dataKey="name" style={{ fontSize: "11px", fontWeight: 600 }} tickLine={false} axisLine={false} />
                            <YAxis style={{ fontSize: "11px" }} tickFormatter={(v) => isCurrency ? `$${v >= 1000 ? (v/1000).toFixed(1)+'k' : v.toFixed(0)}` : v} axisLine={false} tickLine={false} />
                            <Tooltip cursor={{ fill: '#f8f9fa' }} />
                            <Bar dataKey={dataKey} radius={[6, 6, 0, 0]} barSize={35}>
                                {chartData.map((entry: any, index: number) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Paper>
        </Grid>
    );

    return (
        <Box className="dashBoardMain" sx={{ pt: { xs: 2, sm: 3 }, pl: { xs: 2, sm: 6 }, pr: { xs: 2, sm: 5 }, pb: 2, width: "100%" }}>
            <Box sx={{ 
                p: { xs: 2, sm: 3 }, 
                width: "100%", 
                backgroundColor: { xs: "transparent", md: "#9E9E9E1A" }, 
                borderRadius: "12px" 
            }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
                    Channel Performance Rollup — {PROPERTIES.find(p => p.id === propertyId)?.title}
                </Typography>

                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small" sx={{ bgcolor: '#fff', borderRadius: '4px' }}>
                            <InputLabel>Property</InputLabel>
                            <Select value={propertyId} label="Property" onChange={(e) => setPropertyId(e.target.value as string)}>
                                {PROPERTIES.map((p) => <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <DateRangePicker value={dateRange} onChange={(val) => setDateRange(val)} format="yyyy-MM-dd" style={{ width: '100%' }} placeholder="Select Date Range" />
                    </Grid>
                    <Grid item xs={12} sm={12} md={4}>
                        <TagPicker data={CHANNEL_OPTIONS} placeholder="Channel Filters" value={channels} onChange={setChannels} style={{ width: '100%' }} />
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 4, opacity: 0.6 }} />

                {response.loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>
                ) : response.error ? (
                    <Alert severity="error">{response.error}</Alert>
                ) : chartData.length === 0 ? (
                    /* NO DATA FOUND SECTION */
                    <Box sx={{ py: 10, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">No data found for the selected criteria.</Typography>
                        <Typography variant="body2" color="text.secondary">Try adjusting your filters or date range.</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {renderBarChart("Clicks ", "clicks")}
                        {renderBarChart("Impressions ", "impressions")}
                        {renderBarChart("Ad Spend ", "cost", true)}
                        {renderBarChart("Bookings ", "bookings")}
                        {renderBarChart("Revenue ", "revenue", true)}
                        {renderBarChart("ROAS ", "roas")}
                    </Grid>
                )}
            </Box>
        </Box>
    );
};

export default ChannelRollup;